

import React from 'react';
import { Activity, AlertCircle } from 'lucide-react';
import { formatTime } from '@/lib/dateFormat';

interface MetricCardProps {
  title: string;
  metrics: Array<{
    label: string;
    value: string | number;
    unit?: string;
  }>;
  icon?: React.ReactNode;
  status?: 'normal' | 'warning' | 'critical';
  lastUpdated?: Date;
  loading?: boolean;
}

const statusColors = {
  normal: 'border-green-200 bg-green-50',
  warning: 'border-yellow-200 bg-yellow-50',
  critical: 'border-red-200 bg-red-50',
};

const statusIcons = {
  normal: <Activity className="w-4 h-4 text-green-600" />,
  warning: <AlertCircle className="w-4 h-4 text-yellow-600" />,
  critical: <AlertCircle className="w-4 h-4 text-red-600" />,
};

export function MetricCard({
  title,
  metrics,
  icon,
  status = 'normal',
  lastUpdated,
  loading = false,
}: MetricCardProps) {
  return (
    <div className={`rounded-lg border p-4 sm:p-6 transition-all ${statusColors[status]}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon && <div className="text-lg">{icon}</div>}
          <h3 className="font-semibold text-sm sm:text-base">{title}</h3>
        </div>
        {statusIcons[status]}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-3">
        {metrics.map((metric, idx) => (
          <div key={idx} className="bg-white/50 rounded p-2 sm:p-3">
            <p className="text-xs text-gray-600 mb-1">{metric.label}</p>
            {loading ? (
              <div className="h-6 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className="text-lg sm:text-xl font-bold">
                {metric.value}
                {metric.unit && <span className="text-xs ml-1">{metric.unit}</span>}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-xs text-gray-500">
          Updated {formatTime(lastUpdated)}
        </p>
      )}
    </div>
  );
}
